import { motion } from "framer-motion";
import { MapPin, CreditCard, ShoppingBag, Loader2 } from "lucide-react";

interface CartItem {
  cartId: string;
  productId: string;
  name: string;
  price: number;
  qty: number;
  addons: { id: string; name: string; price: number; qty: number; categoryName?: string }[];
}

interface CheckoutReviewSheetProps {
  cart: CartItem[];
  cartTotal: number;
  finalTotal: number;
  couponDiscount: number;
  loyaltyDiscount: number;
  deliveryFee: number;
  orderType: string;
  deliveryAddress: string;
  paymentMethod: string;
  customerName: string;
  restaurantName: string;
  primaryColor: string;
  onConfirm: () => void;
  onEdit: () => void;
  submitting: boolean;
  fmt: (v: number) => string;
  scheduledFor?: string | null;
  splitInfo?: string | null;
}

const paymentLabels: Record<string, string> = {
  pix: "PIX", credit_card: "Cartão de Crédito", debit_card: "Cartão de Débito",
  cash: "Dinheiro", mercadopago: "Cartão (Mercado Pago)", mp_pix: "PIX (Mercado Pago)",
  pay_on_delivery: "Pagar na Entrega",
  pay_on_delivery_pix: "Na Entrega — PIX",
  pay_on_delivery_credit_card: "Na Entrega — Cartão de Crédito",
  pay_on_delivery_cash: "Na Entrega — Dinheiro",
};

const orderTypeLabels: Record<string, { label: string; icon: string }> = {
  dine_in: { label: "Consumir no local", icon: "🍽️" },
  takeout: { label: "Retirada no balcão", icon: "🛍️" },
  delivery: { label: "Entrega", icon: "🛵" },
  scheduled: { label: "Pedido agendado", icon: "📅" },
};

function groupAddonsByCategory(addons: CartItem["addons"]) {
  const grouped: Record<string, typeof addons> = {};
  addons.forEach(ad => {
    const cat = ad.categoryName || "";
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(ad);
  });
  return grouped;
}

export const CheckoutReviewSheet = ({
  cart, cartTotal, finalTotal, couponDiscount, loyaltyDiscount,
  deliveryFee, orderType, deliveryAddress, paymentMethod,
  customerName, restaurantName, primaryColor, onConfirm, onEdit,
  submitting, fmt, scheduledFor, splitInfo,
}: CheckoutReviewSheetProps) => {
  const ot = orderTypeLabels[orderType] || orderTypeLabels.dine_in;

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex-1 min-h-0 overflow-auto p-5 space-y-4">
        {/* Order type */}
        <div className="flex items-center gap-3 py-3">
          <div className="w-10 h-10 rounded-full bg-[hsl(0,0%,96%)] flex items-center justify-center text-lg">
            {ot.icon}
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-[hsl(0,0%,13%)]">{ot.label}</p>
            {orderType === "delivery" ? (
              <p className="text-xs text-[hsl(0,0%,55%)]">Hoje, 40 - 55 min</p>
            ) : (
              <p className="text-xs text-[hsl(0,0%,55%)]">Pronto em ~30 min</p>
            )}
          </div>
        </div>

        {/* Scheduled info */}
        {scheduledFor && (
          <div className="flex items-center gap-3 py-3 border-t border-[hsl(0,0%,95%)]">
            <div className="w-10 h-10 rounded-full bg-[hsl(210,80%,95%)] flex items-center justify-center text-lg">📅</div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-[hsl(0,0%,13%)]">Agendado</p>
              <p className="text-xs text-[hsl(210,80%,40%)]">
                {new Date(scheduledFor).toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long" })} às {new Date(scheduledFor).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
              </p>
            </div>
          </div>
        )}

        {/* Split info */}
        {splitInfo && (
          <div className="flex items-center gap-3 py-3 border-t border-[hsl(0,0%,95%)]">
            <div className="w-10 h-10 rounded-full bg-[hsl(270,60%,95%)] flex items-center justify-center text-lg">👥</div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-[hsl(0,0%,13%)]">Conta dividida</p>
              <p className="text-xs text-[hsl(270,60%,40%)]">{splitInfo}</p>
            </div>
          </div>
        )}

        {/* Delivery address */}
        {orderType === "delivery" && deliveryAddress && (
          <div className="flex items-center gap-3 py-3 border-t border-[hsl(0,0%,95%)]">
            <div className="w-10 h-10 rounded-full bg-[hsl(0,0%,96%)] flex items-center justify-center">
              <MapPin className="h-5 w-5 text-[hsl(0,0%,40%)]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-[hsl(0,0%,13%)] truncate">{deliveryAddress.split(",")[0]}</p>
              <p className="text-xs text-[hsl(0,0%,55%)] truncate">{deliveryAddress.split(",").slice(1).join(",").trim()}</p>
            </div>
          </div>
        )}

        {/* Items */}
        <div className="border-t border-[hsl(0,0%,95%)] pt-3">
          <p className="text-xs font-bold text-[hsl(0,0%,45%)] uppercase tracking-wider mb-3">Itens do pedido</p>
          <div className="space-y-3">
            {cart.map((item) => {
              const addonTotal = item.addons.reduce((a, ad) => a + ad.price * ad.qty, 0);
              const itemTotal = (item.price + addonTotal) * item.qty;
              const grouped = item.addons.length > 0 ? groupAddonsByCategory(item.addons) : {};
              return (
                <div key={item.cartId} className="flex justify-between items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[hsl(0,0%,13%)]">
                      <span className="font-semibold">{item.qty}x</span> {item.name}
                    </p>
                    {item.addons.length > 0 && (
                      <div className="mt-1 space-y-0.5">
                        {Object.entries(grouped).map(([cat, ads]) => (
                          <div key={cat}>
                            {cat && <p className="text-[10px] font-semibold text-[hsl(0,0%,40%)] uppercase tracking-wider mt-0.5">{cat}:</p>}
                            {ads.map((ad) => (
                              <p key={ad.id} className="text-[11px] text-[hsl(0,0%,55%)]">
                                + {ad.name}{ad.qty > 1 ? ` (x${ad.qty})` : ""}
                              </p>
                            ))}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <span className="text-sm font-medium text-[hsl(0,0%,13%)] tabular-nums shrink-0">{fmt(itemTotal)}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Coupon applied */}
        {couponDiscount > 0 && (
          <div className="flex items-center gap-3 py-3 border-t border-[hsl(0,0%,95%)]">
            <div className="w-10 h-10 rounded-full bg-[hsl(270,60%,95%)] flex items-center justify-center">
              <span className="text-lg">🏷️</span>
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-[hsl(0,0%,13%)]">Cupom aplicado</p>
              <p className="text-xs text-[hsl(142,71%,40%)]">Desconto de {fmt(couponDiscount)}</p>
            </div>
          </div>
        )}

        {/* Payment */}
        {paymentMethod && (
          <div className="flex items-center gap-3 py-3 border-t border-[hsl(0,0%,95%)]">
            <div className="w-10 h-10 rounded-full bg-[hsl(0,0%,96%)] flex items-center justify-center">
              <CreditCard className="h-5 w-5 text-[hsl(0,0%,40%)]" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-[hsl(0,0%,13%)]">Pagamento</p>
              <p className="text-xs text-[hsl(0,0%,55%)]">{paymentLabels[paymentMethod] || paymentMethod}</p>
            </div>
          </div>
        )}

        {/* Summary */}
        <div className="p-4 rounded-2xl bg-[hsl(0,0%,97%)] space-y-2">
          <div className="flex justify-between text-sm text-[hsl(0,0%,45%)]">
            <span>Subtotal</span>
            <span className="tabular-nums">{fmt(cartTotal)}</span>
          </div>
          {couponDiscount > 0 && (
            <div className="flex justify-between text-sm font-medium text-[hsl(0,80%,45%)]">
              <span>Cupom</span>
              <span className="tabular-nums">-{fmt(couponDiscount)}</span>
            </div>
          )}
          {loyaltyDiscount > 0 && (
            <div className="flex justify-between text-sm font-medium text-[hsl(142,71%,40%)]">
              <span>Recompensas</span>
              <span className="tabular-nums">-{fmt(loyaltyDiscount)}</span>
            </div>
          )}
          {deliveryFee > 0 && (
            <div className="flex justify-between text-sm text-[hsl(0,0%,45%)]">
              <span>Taxa de entrega</span>
              <span className="tabular-nums">{fmt(deliveryFee)}</span>
            </div>
          )}
          <div className="flex justify-between text-base font-bold text-[hsl(0,0%,10%)] pt-2 border-t border-[hsl(0,0%,90%)]">
            <span>Total</span>
            <span className="tabular-nums">{fmt(finalTotal)}</span>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="px-5 pb-5 pt-2 space-y-2 border-t border-[hsl(0,0%,95%)] bg-[hsl(0,0%,100%)]">
        <button
          onClick={onConfirm}
          disabled={submitting}
          className="w-full h-[52px] rounded-2xl font-bold text-[15px] text-white disabled:opacity-40 transition-all active:scale-[0.97] shadow-lg"
          style={{ backgroundColor: primaryColor, boxShadow: `0 6px 20px ${primaryColor}50` }}
        >
          {submitting ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin" />
              Processando...
            </span>
          ) : (
            `Fazer pedido · ${fmt(finalTotal)}`
          )}
        </button>
        <button
          onClick={onEdit}
          className="w-full py-3 text-sm font-semibold transition-colors text-center"
          style={{ color: primaryColor }}
        >
          Alterar pedido
        </button>
      </div>
    </div>
  );
};
